import { useTransactionForms } from './transactions/useTransactionForms';
import { useRelatedTransactions } from './transactions/useRelatedTransactions';
import { useTransactionDetails } from './transactions/useTransactionDetails';
import { useTransactionActions } from './transactions/useTransactionActions';
import { useTransactionsStore } from '#imports';
import { cardNetworkScheme, digitalWalletScheme } from '~/utils/transactions/transaction-schemes';
import { buildTransactionErrorDescriptions } from '~/utils/transactions/transaction-error-descriptions';

// Facade keeping the original public API stable; delegates each concern to a focused sub-composable.
export const useTransactions = () => {
  const { $i18n } = useNuxtApp();
  const { t, te } = $i18n;
  const route = useRoute();
  const { walletData } = storeToRefs(useTransactionsStore());

  const forms = useTransactionForms();
  const related = useRelatedTransactions();
  const details = useTransactionDetails(related);
  const actions = useTransactionActions({
    forms,
    getTransactionInfos: details.getTransactionInfos
  });

  const getTransactionErrorDescriptions = (pspName: string, error?: string): string[] =>
    buildTransactionErrorDescriptions(pspName, error, t, te);

  return {
    // forms
    sendPaymentBody: forms.sendPaymentBody,
    currency: forms.currency,
    options: forms.options,
    showCredit: forms.showCredit,
    initialPaymentBody: forms.initialPaymentBody,
    initialSimulateMoneyInBody: forms.initialSimulateMoneyInBody,
    selectedPaymentMethod: forms.selectedPaymentMethod,
    handleLoadDebitAccount: forms.handleLoadDebitAccount,
    handleLoadCreditAccount: forms.handleLoadCreditAccount,
    handleLoadTransaction: forms.handleLoadTransaction,
    loadWalletByExtId: forms.loadWalletByExtId,

    // details
    walletData,
    transactionId: details.transactionId,
    transactionDetailsTabs: details.transactionDetailsTabs,
    isBankTransferMoneyOut: details.isBankTransferMoneyOut,
    isPspMethod: details.isPspMethod,
    getTransactionBankAccountInfos: details.getTransactionBankAccountInfos,
    getTransactionInfos: details.getTransactionInfos,
    getP2PTransactionInfos: details.getP2PTransactionInfos,

    // related transactions
    loadRelatedTransactions: related.loadRelatedTransactions,
    loadP2PRelatedTransactions: related.loadP2PRelatedTransactions,

    // actions & modals
    transactionDetailsActions: actions.transactionDetailsActions,
    toggleSendPaymentModal: actions.toggleSendPaymentModal,
    showSendPaymentModal: actions.showSendPaymentModal,
    handleSendPayment: actions.handleSendPayment,
    toggleRefundPaymentModal: actions.toggleRefundPaymentModal,
    handleRefundPayment: actions.handleRefundPayment,
    toggleConfirmActionModal: actions.toggleConfirmActionModal,
    toggleCaptureTransactionModal: actions.toggleCaptureTransactionModal,
    toggleSimulateMoneyInModal: actions.toggleSimulateMoneyInModal,
    toggleChargeBackModal: actions.toggleChargeBackModal,

    // pure helpers
    getTransactionErrorDescriptions,
    digitalWalletScheme,
    cardNetworkScheme,

    route
  };
};
